import { createContext, useContext, useEffect, useState } from 'react'
import { useReportsContext } from './Reports.context'
import { InformacionDialog } from '../dialogs'
import { useMachineContext } from './Machines.context'
import { ConfirmarDialog } from '../dialogs/Confirmar.dialog'
import { useAlertContext } from './Alert.context'
import apiClient from '../connections/api' // For version check
import { useNavigate, useLocation } from 'react-router-dom' // For version mismatch navigation
import packageInfo from '../../package.json'
import { useAuth } from './AuthContext'

const frontendVersion = packageInfo.version

export const SystemContext = createContext()

export const SystemProvider = props => {
    const { pautas } = useReportsContext()
    const { machines } = useMachineContext()
    const { isAuthenticated } = useAuth()
    const [openInfoModal, setOpenInfoModal] = useState(false)
    const [datainfo, setDataInfo] = useState()

    // States moved from Auth.context.jsx
    const [openConfirmar, setOpenConfirmar] = useState(false)
    const [mensajeConfirmar, setMensajeConfirmar] = useState('')
    const [backendVersion, setBackendVersion] = useState()

    const navigate = useNavigate() // Hook for navigation
    const location = useLocation()
    const { setConfirm } = useAlertContext() // From Alert.context.jsx

    useEffect(() => {
        const data = {
            pautasDescargadas: pautas.length,
            maquinasDescargadas: machines.length
        }
        setDataInfo(data)
    }, [pautas, machines]) // Added machines to dependency array for completeness

    // Effect to get version when component mounts (or at some trigger)
    useEffect(() => {
        if (!isAuthenticated) return
        getVersion()
        checkFrontendVersion()
    }, [isAuthenticated])

    const closeModalInfo = () => {
        setOpenInfoModal(false)
    }

    const checkFrontendVersion = () => {
        const localFrontendVersion = localStorage.getItem('frontend_version')
        if (!localFrontendVersion) {
            localStorage.setItem('frontend_version', frontendVersion)
            return
        }
        if (localFrontendVersion !== frontendVersion) {
            console.log(`[SystemContext] New frontend version detected: ${frontendVersion}`)
            setConfirm('Aviso Importante!', 'Se ha detectado una nueva versión del frontend. Por favor, cierre sesión y vuelva a ingresar.', () => {
                localStorage.clear()
                localStorage.setItem('frontend_version', frontendVersion)
                window.location.reload()
            })
        }
    }

    const getVersion = async () => {
        try {
            const response = await apiClient.get('/version')
            const newVersion = response?.data?.version

            if (!newVersion) {
                console.error('Could not retrieve version from server.')
                const defaultVersion = '1.0.0'
                localStorage.setItem('version', defaultVersion)
                setBackendVersion(defaultVersion)
                return
            }

            setBackendVersion(newVersion)
            const versionLocal = localStorage.getItem('version')

            if (versionLocal) {
                if (versionLocal.toString() !== newVersion.toString()) {
                    setConfirm('Aviso Importante!', 'Se ha detectado una nueva versión del sistema. Se cerrará sesión para actualizar.', () => {
                        navigate('/login', { replace: true })
                        localStorage.clear()
                        localStorage.setItem('version', newVersion)
                        localStorage.setItem('frontend_version', frontendVersion)
                        window.location.reload()
                    })
                }
            } else {
                localStorage.setItem('version', newVersion)
            }
        } catch (error) {
            console.error('Failed to get version:', error)
            const defaultVersion = '1.0.0'
            if (!localStorage.getItem('version')) {
                localStorage.setItem('version', defaultVersion)
                setBackendVersion(defaultVersion)
            }
        }
    }

    const activarConfirmacion = (message, select) => {
        setOpenConfirmar(true)
        setMensajeConfirmar(message)
        // Original returned a component, which is an anti-pattern.
        // It should just set state. Component will be rendered based on state.
        // This will be handled in the return statement of the Provider.
    }

    const provider = {
        setOpenInfoModal,
        activarConfirmacion, // Export this function
        versionNumber: backendVersion, // Maintain compatibility
        backendVersion,
        frontendVersion
    }

    return (
        <>
            <SystemContext.Provider value={provider} {...props} />
            {
                openInfoModal &&
            <InformacionDialog
                open={openInfoModal}
                handleClose={closeModalInfo}
                data={datainfo}
            />
            }
            {
                openConfirmar && // Render ConfirmarDialog based on state
            <ConfirmarDialog
                open={openConfirmar}
                message={mensajeConfirmar}
                handleClose={() => setOpenConfirmar(false)}
            />
            }
        </>
    )
}

export const useSystemContext = () => useContext(SystemContext)
